import { ButtonGroup } from "@/components/_atoms/button-group";
import { Label } from "@/components/_atoms/label";
import { Popover, PopoverPortalContent, PopoverTrigger } from "@/components/_atoms/popover";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/_atoms/select";
import { QuickSearchInput } from "@/components/_molecules/quick-search-input";
import { ToolbarFilterButton } from "@/components/_molecules/toolbar-filter-button";
import { FeatureFlagToggle } from "@/domain/_shared/feature-flag/components/feature-flag-toggle";
import { useDomainTranslation } from "@/domain/_shared/i18n/use-domain-translation";
import { MunicipalPropertyCatalogFilter } from "@/domain/municipal-property/models/municipal-property-catalog-filter.model";
import { WorkspaceMunicipalityOption } from "@/domain/municipal-property/models/workspace-municipality-option.model";
import { buildDatatableToolbarRowClass } from "@/domain/view/components/datatable/datatable-toolbar-row-class";
import { MunicipalPropertyLifecycleFilter } from "@/graphql/generated/graphql";
import { MUNICIPAL_PROPERTY_CONSUMPTION_FEATURE_FLAG_ID } from "@ark/shared";

const ALL_MUNICIPALITIES_VALUE = "__all__";
const MUNICIPALITY_SELECT_ID = "municipal-property-catalog-filter-municipality";
const LIFECYCLE_SELECT_ID = "municipal-property-catalog-filter-lifecycle";
const USAGE_SELECT_ID: string = "municipal-property-catalog-filter-usage";

const LIFECYCLE_OPTION_LIST = [
  MunicipalPropertyLifecycleFilter.Active,
  MunicipalPropertyLifecycleFilter.Archived,
  MunicipalPropertyLifecycleFilter.All,
] as const;

interface MunicipalPropertyCatalogFilterBarProps {
  filter: MunicipalPropertyCatalogFilter;
  municipalityList: readonly WorkspaceMunicipalityOption[];
  onSearchChange: (search: string) => void;
  onMunicipalityChange: (municipalityId: string | null) => void;
  onUsageChange: (usage: MunicipalPropertyCatalogFilter["usage"]) => void;
  onLifecycleChange: (lifecycle: MunicipalPropertyLifecycleFilter) => void;
}

export function MunicipalPropertyCatalogFilterBar({
  filter,
  municipalityList,
  onSearchChange,
  onMunicipalityChange,
  onUsageChange,
  onLifecycleChange,
}: MunicipalPropertyCatalogFilterBarProps) {
  const { t } = useDomainTranslation("municipal-property");

  return (
    <div className={buildDatatableToolbarRowClass(false)}>
      <ButtonGroup className="w-full min-w-0 flex-1">
        <QuickSearchInput value={filter.search} onChange={onSearchChange} />
        <Popover>
          <PopoverTrigger asChild={true}>
            <ToolbarFilterButton />
          </PopoverTrigger>
          <PopoverPortalContent side="bottom" align="end" className="flex w-72 flex-col gap-3 p-3">
            {municipalityList.length > 0 ? (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor={MUNICIPALITY_SELECT_ID}>{t("catalog.filter.municipality")}</Label>
                <Select
                  value={filter.municipalityId ?? ALL_MUNICIPALITIES_VALUE}
                  onValueChange={(value) => onMunicipalityChange(value === ALL_MUNICIPALITIES_VALUE ? null : value)}
                >
                  <SelectTrigger id={MUNICIPALITY_SELECT_ID} className="h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL_MUNICIPALITIES_VALUE}>{t("catalog.filter.allMunicipalities")}</SelectItem>
                    {municipalityList.map((municipality) => (
                      <SelectItem key={municipality.id} value={municipality.id}>
                        {municipality.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}
            <FeatureFlagToggle flag={MUNICIPAL_PROPERTY_CONSUMPTION_FEATURE_FLAG_ID} isDevelopmentBypassAllowed={false}>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor={USAGE_SELECT_ID}>{t("catalog.filter.usage")}</Label>
                <Select
                  value={filter.usage}
                  onValueChange={(value: string): void =>
                    onUsageChange(value as MunicipalPropertyCatalogFilter["usage"])
                  }
                >
                  <SelectTrigger id={USAGE_SELECT_ID} className="h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value="ALL">{t("catalog.filter.usage_ALL")}</SelectItem>
                      <SelectItem value="USED">{t("catalog.filter.usage_USED")}</SelectItem>
                      <SelectItem value="UNUSED">{t("catalog.filter.usage_UNUSED")}</SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
            </FeatureFlagToggle>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={LIFECYCLE_SELECT_ID}>{t("catalog.filter.lifecycle")}</Label>
              <Select
                value={filter.lifecycle}
                onValueChange={(value) => onLifecycleChange(value as MunicipalPropertyLifecycleFilter)}
              >
                <SelectTrigger id={LIFECYCLE_SELECT_ID} className="h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LIFECYCLE_OPTION_LIST.map((lifecycle) => (
                    <SelectItem key={lifecycle} value={lifecycle}>
                      {t(`catalog.filter.lifecycle_${lifecycle}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </PopoverPortalContent>
        </Popover>
      </ButtonGroup>
    </div>
  );
}
